import { z } from 'zod';

import { boolFromQuery } from './_query';

/**
 * Notification kinds — must stay in sync with `NotificationCreatedEvent.kind`
 * in `events.ts` and the CHECK constraint in migration `0009_init_notifications.sql`
 * (extended in 0039 to add the chat kinds).
 *
 * Phase 2 / Step 12 wired producers for: assignment, mention, reply,
 * status_changed, added_to_team, added_to_project, role_changed, team_invitation.
 * Phase 6 follow-up adds the chat group — `chat_mention`, `chat_thread_reply`,
 * `chat_reaction`, `chat_message_in_subscribed_topic`.  See migration 0039.
 * The remaining values (`due_soon`, `overdue`, `reminder`, `watcher_added`,
 * `system`, `ai_suggestion`) are reserved for Phase 5 cron workers / AI surface
 * and accepted by the schema today so the column never needs another ALTER.
 */
export const NOTIFICATION_KINDS = [
  'mention',
  'assignment',
  'reply',
  'due_soon',
  'overdue',
  'reminder',
  'status_changed',
  'added_to_team',
  'added_to_project',
  'role_changed',
  'team_invitation',
  'watcher_added',
  'system',
  'ai_suggestion',
  'chat_mention',
  'chat_thread_reply',
  'chat_reaction',
  'chat_message_in_subscribed_topic',
  // Phase 4 polish — Approval Center inbox fanout (migration 0074).
  'approval_pending',
  'approval_resolved',
  // Sprint 1.2/1.3 — stranger DM landed in your requests inbox (migration 0089).
  'message_request',
  // W3.2 — a reply in an existing DM thread badges the recipient's inbox.
  'dm_message',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * Inbox importance tier (Sprint 1.3, migration 0089).
 *   'action'   — needs you to do something (assignment, invite, approval,
 *                @mention, reply, message request).
 *   'activity' — FYI / ambient (status changes, watched comments, reactions,
 *                added-to-project, AI digests…).
 * A flood of low-tier activity must never bury an action item.
 */
export const NOTIFICATION_TIERS = ['action', 'activity'] as const;
export type NotificationTier = (typeof NOTIFICATION_TIERS)[number];

/**
 * Notification origin (Sprint 1.3).  'ai' notifications render as their own
 * category (agent finished, digest ready, anomaly/risk) — separate from the
 * human/system stream.
 */
export const NOTIFICATION_SOURCES = ['system', 'ai'] as const;
export type NotificationSource = (typeof NOTIFICATION_SOURCES)[number];

/** Canonical kind → tier mapping (single source of truth for BE fanout + FE
 *  grouping; the migration 0089 backfill mirrors this in SQL). */
export const KIND_TIER: Record<NotificationKind, NotificationTier> = {
  // action — directed at you, needs a response
  mention: 'action',
  reply: 'action',
  assignment: 'action',
  approval_pending: 'action',
  team_invitation: 'action',
  chat_mention: 'action',
  chat_thread_reply: 'action',
  message_request: 'action',
  dm_message: 'action',
  // activity — ambient / FYI
  status_changed: 'activity',
  added_to_team: 'activity',
  added_to_project: 'activity',
  role_changed: 'activity',
  watcher_added: 'activity',
  due_soon: 'activity',
  overdue: 'activity',
  reminder: 'activity',
  chat_reaction: 'activity',
  chat_message_in_subscribed_topic: 'activity',
  approval_resolved: 'activity',
  ai_suggestion: 'activity',
  system: 'activity',
};

export const NOTIFICATION_SOURCE_TYPES = [
  'task',
  'comment',
  'project',
  'team',
  'invitation',
  'system',
  'chat_message',
  // Phase 4 polish — Approval Center inbox fanout (migration 0074).
  'approval',
  // Sprint 1.3 — DM message request (migration 0089).
  'message_request',
  // W3.2 — reply in an existing DM thread (migration 0094).
  'dm_message',
] as const;
export type NotificationSourceType = (typeof NOTIFICATION_SOURCE_TYPES)[number];

// ── Inbox listing ───────────────────────────────────────────────────────────

export const InboxQuerySchema = z.object({
  kind: z.enum(NOTIFICATION_KINDS).optional(),
  /** Filter to one importance tier (Sprint 1.3). */
  tier: z.enum(NOTIFICATION_TIERS).optional(),
  /** Filter to one origin (e.g. 'ai' for the AI category). */
  source: z.enum(NOTIFICATION_SOURCES).optional(),
  /**
   * Filter to unread only.  Uses boolFromQuery() — `z.coerce.boolean()` is
   * WRONG here (`Boolean('false') === true`), which made `?unread=false`
   * filter to unread-only and hide every read notification (the inbox
   * appeared to empty out on read).
   */
  unread: boolFromQuery().optional(),
  /** Cursor: ISO timestamp of the last seen row's `createdAt`. */
  cursor: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type InboxQuery = z.infer<typeof InboxQuerySchema>;

/**
 * One inbox row.  `payload` is intentionally `unknown` here — the shape varies
 * per `kind` and clients render based on `kind` + `sourceType` + `sourceId`.
 * Specific payload shapes are documented in `FanoutService` (apps/api).
 */
export const NotificationProjectionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  kind: z.enum(NOTIFICATION_KINDS),
  tier: z.enum(NOTIFICATION_TIERS),
  source: z.enum(NOTIFICATION_SOURCES),
  sourceType: z.enum(NOTIFICATION_SOURCE_TYPES),
  sourceId: z.string().uuid().nullable(),
  teamId: z.string().uuid().nullable(),
  payload: z.record(z.string(), z.unknown()),
  coalesceCount: z.number().int().min(1),
  readAt: z.string().datetime().nullable(),
  snoozedUntil: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type NotificationProjection = z.infer<typeof NotificationProjectionSchema>;

export const InboxPageSchema = z.object({
  items: z.array(NotificationProjectionSchema),
  /** ISO of the last item's `createdAt`; null when end of feed. */
  nextCursor: z.string().datetime().nullable(),
});
export type InboxPage = z.infer<typeof InboxPageSchema>;

// ── Mutations ───────────────────────────────────────────────────────────────

/**
 * Snooze targets:
 *   'today'     → end of today UTC.
 *   'tomorrow'  → end of tomorrow UTC.
 *   'next_week' → next Monday 09:00 UTC.
 *   ISO        → explicit timestamp (in the future).
 */
export const SnoozePresetSchema = z.enum(['today', 'tomorrow', 'next_week']);
export type SnoozePreset = z.infer<typeof SnoozePresetSchema>;

export const SnoozeRequestSchema = z.object({
  until: z.union([SnoozePresetSchema, z.string().datetime()]),
});
export type SnoozeRequest = z.infer<typeof SnoozeRequestSchema>;

export const MarkAllReadQuerySchema = z.object({
  kind: z.enum(NOTIFICATION_KINDS).optional(),
});
export type MarkAllReadQuery = z.infer<typeof MarkAllReadQuerySchema>;

export const UnreadCountSchema = z.object({
  count: z.number().int().min(0),
});
export type UnreadCount = z.infer<typeof UnreadCountSchema>;

/**
 * Per-team rollup metrics — powers the team-row subtitles in the
 * sidebar / teams-list / mobile Workspace level 1 and the Home team pulse.
 * Returned as an array (one row per team I belong to).  Counts are over
 * tasks I can see, using the shared definitions in `metrics/` (§2.3).
 * Fields added in Wave 2 default to 0 so older servers still parse.
 */
export const TeamMetricSchema = z.object({
  teamId: z.string().uuid(),
  projectCount: z.number().int().min(0),
  openTasks: z.number().int().min(0),
  overdueTasks: z.number().int().min(0),
  /** Open tasks whose deadline falls in my current calendar week (`isDueThisWeek`). */
  dueThisWeek: z.number().int().min(0).default(0),
  /** Open tasks with an Open BLOCKED_BY blocker (`isBlocked`). */
  blocked: z.number().int().min(0).default(0),
  /** Milestones at risk (`isMilestoneAtRisk`). */
  atRiskMilestones: z.number().int().min(0).default(0),
});
export type TeamMetric = z.infer<typeof TeamMetricSchema>;

/**
 * Dashboard "Requires my attention" widget — quick counts that answer
 * "what's waiting on me right now?"  Each is independent; zero-counts
 * mean nothing to surface.  Click-through routes are decided FE-side.
 *
 * Task counts use the shared definitions in `metrics/` (§2.3) over my
 * set (`isMine`, never author-only).  `dueToday` and `approvals` were
 * added in Wave 2 and default to 0 so older servers still parse.
 */
export const MeAttentionSchema = z.object({
  /** To review: Open ∧ respondent = me ∧ category = review (`isToReview`). */
  reviewing: z.number().int().min(0),
  /** Due soon: mine, not overdue, deadline's local date ≤ tomorrow (`isDueSoon`). */
  dueSoon: z.number().int().min(0),
  /** Overdue: mine, deadline < now (`isOverdue`). */
  overdue: z.number().int().min(0),
  /** Pending team invitations addressed to my email. */
  invitations: z.number().int().min(0),
  /** Unread inbox notifications of kind='mention'. */
  mentions: z.number().int().min(0),
  /** Due today: mine, not overdue, deadline's local date = today (`isDueToday`). */
  dueToday: z.number().int().min(0).default(0),
  /** Pending approval requests I'm eligible to decide. */
  approvals: z.number().int().min(0).default(0),
});
export type MeAttention = z.infer<typeof MeAttentionSchema>;
